// ============================================================================
// INTÉGRATIONS — les services tiers que la plateforme utilise pour tous les magasins, un onglet par service.
//
// LE PARTAGE (décision du 12/10, maquette) : Vue d'ensemble · Messagerie · SMS · Portefeuille. On y vient toujours
// avec un OUTIL en tête (« je veux brancher les SMS »), jamais avec un lieu de stockage. Les sondes de NOS
// applications n'y sont plus : elles vivent sur la page Applications, avec leur connexion à l'IdP — « Supervision »
// désignait ici une chose et sur une autre page une autre. Le transfert wallet a rejoint le Portefeuille, qui retrouve
// ses identifiants et sa file au même endroit.
//
// ⚠️ LA VUE D'ENSEMBLE NE RECALCULE RIEN : elle lit ce que chaque carte rapporte (état, nom, rôle, stockage — voir
//    `useReportIntegration`). Les onglets restent donc MONTÉS, simplement cachés : une carte démontée ne rapporterait
//    plus, la synthèse compterait de moins, et une épreuve faite dans l'onglet SMS disparaîtrait au retour.
// ⚠️ LE LIEU DE STOCKAGE N'A PAS DISPARU : une base magasin appartient au commerçant, et nos comptes fournisseurs n'y
//    entrent jamais. C'est écrit au pied de chaque carte.
// ============================================================================
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Tabs } from '@orgazone/ui-web';
import { api, type BoPlatformSetting, type BoSettingsState, type MailSettings } from '../api';
import { MailForm } from './settings-mail-form';
import { WalletPanel } from './settings-wallet';
import {
  CarteEsendex, CarteIntegration, CarteSmsEnvoi, CarteWallet, IntegrationReportContext, PastilleEtat, SyntheseIntegrations,
  type IntegrationReport, type IntegrationTab,
} from './settings-integrations';

// ── LA MESSAGERIE, DEUX FOIS — mais côte à côte et nommées sans ambiguïté ─────────────────────────
//
// ⚠️ CE SONT BIEN DEUX RÉGLAGES DISTINCTS, et c'était la première cause de confusion : la Console
// envoie ses invitations de dépôt, le back-office envoie ses e-mails à lui. Les rapprocher ne les
// fusionne pas — ça rend leur différence lisible, avec un mot qui dit CE QUI CASSE si on se trompe.
// ⚠️ L'IdP n'est PAS réglable ici (décision 2026-08-15) : qui contrôle son SMTP contrôle les
// réinitialisations de mot de passe de tous les comptes. Il garde son `.env`, et l'écran le dit.

/** La messagerie de la CONSOLE — invitations de dépôt, notifications d'exploitation. */
function MessagerieConsole() {
  const [data, setData] = useState<MailSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { api.mailSettings().then(setData).catch((e) => setErr((e as Error).message)); }, []);

  // ⚠️ L'erreur PASSE AVANT le chargement : sinon un endpoint HS laisse la carte sur « Chargement… »
  //    pour toujours, l'erreur posée mais jamais rendue (échec avalé, §23.0).
  if (err) return <div className="dcard" style={{ padding: 18, marginBottom: 16 }}><div className="err">Messagerie de la Console indisponible : {err}</div></div>;
  if (!data) return <div className="dcard" style={{ padding: 18, marginBottom: 16 }}><span className="muted">Chargement de la messagerie de la Console…</span></div>;

  return (
    <MailForm
      data={data}
      icone="mail"
      stockage="Base de la Console."
      onSave={(v) => api.updateMailSettings(v)}
      onTest={(to) => api.testMail(to)}
      texts={{
        title: 'Messagerie — Console',
        intro: <>
          D'où partent les <b>invitations de dépôt</b> et les notifications de la Console. Mal réglée,
          un prospect ne reçoit jamais son lien de formulaire. La configuration serveur reste le
          <b> filet</b> si ce réglage est incomplet.
        </>,
        footnote: <>
          ⚠️ Ce réglage ne concerne <b>que la Console</b>. La messagerie de l'IdP reste dans sa propre
          configuration serveur, à dessein : qui contrôle son expéditeur contrôle les réinitialisations
          de mot de passe de tous les comptes.
        </>,
      }}
    />
  );
}

/** Nom lisible d'une famille de réglages du back-office, quand aucune carte dédiée ne la porte. */
const FAMILLES_BO: Record<string, string> = {
  esendex: 'Esendex',
  smsenvoi: 'SMS Envoi',
  wallet: 'Eldowallet',
};

/** Les onglets, dans l'ordre de la page — c'est aussi l'ordre de la vue d'ensemble. */
const TABS: readonly { id: IntegrationTab; label: string }[] = [
  { id: 'all', label: 'Vue d’ensemble' },
  { id: 'mail', label: 'Messagerie' },
  { id: 'sms', label: 'SMS' },
  { id: 'wallet', label: 'Portefeuille' },
];

/** L'introduction d'un onglet : ce qu'il réunit, en une phrase. */
function TabIntro({ children }: { children: ReactNode }) {
  return <p className="muted" style={{ fontSize: 'var(--t-body)', marginTop: 0, lineHeight: 1.55, maxWidth: 760 }}>{children}</p>;
}

/** Une ligne de la vue d'ensemble : ce que la carte a rapporté, et le chemin vers son onglet. */
function OverviewRow({ r, onGo }: { r: IntegrationReport; onGo?: () => void }) {
  const body = (
    <>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink900)' }}>{r.name}</span>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{r.provider}</span>
        <PastilleEtat etat={r.etat} />
      </span>
      <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 3, lineHeight: 1.5 }}>{r.role}</span>
      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink400)', marginTop: 3 }}>{r.storage}</span>
    </>
  );
  const rowSt = { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 18px', borderTop: '1px solid var(--ink200)', textAlign: 'left' as const, color: 'inherit', font: 'inherit' };
  if (!onGo) return <div data-overview={r.key} style={rowSt}><span style={{ minWidth: 0, flex: 1 }}>{body}</span></div>;
  return (
    <button type="button" data-overview={r.key} onClick={onGo} className="row-nav" style={{ ...rowSt, background: 'transparent', border: 'none', borderTop: '1px solid var(--ink200)', cursor: 'pointer' }}>
      <span style={{ minWidth: 0, flex: 1 }}>{body}</span>
      <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--teal-500)', whiteSpace: 'nowrap' }}>régler →</span>
    </button>
  );
}

export function Settings() {
  const [onglet, setOnglet] = useState<IntegrationTab>('all');
  const [state, setState] = useState<BoSettingsState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reports, setReports] = useState<Record<string, IntegrationReport>>({});

  const report = useCallback((r: IntegrationReport) => { setReports((m) => ({ ...m, [r.key]: r })); }, []);
  // Un contexte par onglet, stable : la carte sait où elle est posée sans qu'on le lui passe à la main.
  const contexts = useMemo(
    () => Object.fromEntries(TABS.map((t) => [t.id, { tab: t.id, report }])) as Record<IntegrationTab, { tab: IntegrationTab; report: typeof report }>,
    [report],
  );

  const load = () => api.boSettings()
    .then((r) => { setState(r); setErr(null); })
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { void load(); }, []);

  const write = async (key: string, v: { value?: string | null; secret?: string }) => {
    await api.updateBoSetting({ key, ...v });
    await load();
  };

  // Familles dérivées des clés (`smsenvoi.account` → `smsenvoi`) : le catalogue vit dans le
  // back-office, cet écran ne fait que le rendre.
  const familles = new Map<string, BoPlatformSetting[]>();
  for (const s of state?.platform ?? []) {
    const f = s.key.split('.')[0] ?? s.key;
    familles.set(f, [...(familles.get(f) ?? []), s]);
  }
  const esendex = familles.get('esendex') ?? [];
  const sms = familles.get('smsenvoi') ?? [];
  const wallet = familles.get('wallet') ?? [];
  // ⚠️ CE QU'ON NE SAIT PAS PRÉSENTER RESTE VISIBLE : une famille ajoutée au catalogue du back-office
  //    apparaît dans une carte générique (vue d'ensemble) plutôt que de disparaître en silence.
  const autres = [...familles.entries()].filter(([f]) => !['esendex', 'smsenvoi', 'wallet'].includes(f));

  const order = TABS.map((t) => t.id);
  // ⚠️ L'ordre des onglets, puis le nom et le fournisseur — jamais l'ordre d'ARRIVÉE des rapports, qui suit celui des
  //    réponses du réseau : deux chargements de la même page ne montreraient pas la même liste.
  const listed = Object.values(reports).sort((a, b) => order.indexOf(a.tab) - order.indexOf(b.tab)
    || a.name.localeCompare(b.name, 'fr') || a.provider.localeCompare(b.provider, 'fr'));
  const inTab = (t: IntegrationTab) => listed.filter((r) => r.tab === t).length;
  // Un onglet d'une seule carte n'a pas de compteur : il ne dirait rien de plus que son nom.
  const countOf = (t: IntegrationTab) => (t === 'all' ? listed.length : inTab(t) > 1 ? inTab(t) : undefined);
  // ⚠️ LE MOTIF RÉEL, JAMAIS « indisponible » tout court : back-office non configuré, injoignable ou
  //    secret de service refusé se corrigent à trois endroits différents. L'écran doit dire lequel.
  const boErr = err && <div className="err" style={{ marginBottom: 14 }}>Réglages du back-office indisponibles : {err}</div>;
  const boLoading = !state && !err && <div className="muted" style={{ marginBottom: 14 }}>Chargement des réglages du back-office…</div>;

  return (
    <div className="page">
      <div style={{ marginBottom: 16 }}>
        <h1 className="page-title">Intégrations</h1>
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3, maxWidth: 820, lineHeight: 1.5 }}>
          Les services tiers que la plateforme utilise pour tous les magasins. Chaque carte dit son état réel, ce qui casse
          sans elle, et où le réglage est stocké. Les sondes de nos propres applications sont sur la page Applications.
        </div>
      </div>
      <Tabs
        items={TABS.map((t) => ({ id: t.id, label: t.label, count: countOf(t.id) }))}
        value={onglet}
        onChange={setOnglet}
        panelId="console-integrations"
        label="Services intégrés"
        size="compact"
      />
      <div id="console-integrations" role="tabpanel" aria-labelledby={`console-integrations-tab-${onglet}`} style={{ marginTop: 16 }}>
        {/* ── Vue d'ensemble : ce qui attend quelque chose, sans traverser chaque carte ── */}
        <section data-integrations-panel="all" hidden={onglet !== 'all'}>
          {boErr}
          {/* La synthèse ne lit QUE ce que les cartes ont rapporté : elle ne peut donc pas les contredire. */}
          <SyntheseIntegrations etats={listed.map((r) => r.etat)} />
          {listed.some((r) => r.tab !== 'all') && (
            <div className="dcard" style={{ overflow: 'hidden', marginBottom: 16 }}>
              {listed.filter((r) => r.tab !== 'all').map((r) => <OverviewRow key={r.key} r={r} onGo={() => setOnglet(r.tab)} />)}
            </div>
          )}
          <IntegrationReportContext.Provider value={contexts.all}>
            {autres.map(([f, lines]) => (
              <CarteIntegration
                key={f} icone="api" heading={FAMILLES_BO[f] ?? f} fournisseur={f}
                etat={lines.some((s) => s.value) ? { k: 'configure' } : { k: 'absent' }}
                stockage="Base interne du back-office."
                role={<>Intégration déclarée par le back-office, sans carte dédiée dans cette version de la Console.</>}
              >
                {lines.map((s) => (
                  <div key={s.key} style={{ padding: '13px 18px', borderTop: '1px solid var(--ink200)' }}>
                    <div style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{s.note}</div>
                    <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>{s.key}</div>
                  </div>
                ))}
              </CarteIntegration>
            ))}
          </IntegrationReportContext.Provider>
        </section>

        {/* ── Messagerie : deux réglages distincts, côte à côte et nommés sans ambiguïté ── */}
        <IntegrationReportContext.Provider value={contexts.mail}>
          <section data-integrations-panel="mail" hidden={onglet !== 'mail'}>
            <TabIntro>
              La Console envoie ses invitations de dépôt, le back-office envoie ses e-mails à lui. Les rapprocher ne les
              fusionne pas : chaque carte dit ce qui casse si on règle la mauvaise. L'IdP, lui, n'est pas réglable ici —
              qui contrôle son expéditeur contrôle les réinitialisations de mot de passe de tous les comptes.
            </TabIntro>
            {boErr}{boLoading}
            {state && (
              <MailForm
                data={state.mail}
                icone="mail"
                stockage="Base interne du back-office — jamais dans une base magasin."
                onSave={(v) => api.updateBoMail(v).then((r) => r.mail)}
                onTest={(to) => api.testBoMail(to)}
                texts={{
                  title: 'Messagerie — Back-office',
                  intro: <>
                    D'où partent les e-mails du <b>back-office</b> : réinitialisations de mot de passe des
                    utilisateurs, notifications d'exploitation. Mal réglée, un commerçant ne peut plus récupérer
                    son accès.
                  </>,
                  footnote: <>
                    ⚠️ Réglé ici, appliqué <b>là-bas</b> : la Console le transmet par l'API interne du back-office,
                    qui peut vivre sur une autre machine — elle n'écrit dans aucune base qui ne lui appartient.
                  </>,
                }}
              />
            )}
            <MessagerieConsole />
          </section>
        </IntegrationReportContext.Provider>

        {/* ── SMS : Esendex en tête — c'est la plateforme active —, SMS Envoi gelé ensuite ── */}
        <IntegrationReportContext.Provider value={contexts.sms}>
          <section data-integrations-panel="sms" hidden={onglet !== 'sms'}>
            {boErr}{boLoading}
            {esendex.length > 0 && <CarteEsendex settings={esendex} onWrite={write} />}
            {sms.length > 0 && <CarteSmsEnvoi settings={sms} onWrite={write} />}
          </section>
        </IntegrationReportContext.Provider>

        {/* ── Portefeuille : ses identifiants, et la file du transfert réunie avec eux ── */}
        <IntegrationReportContext.Provider value={contexts.wallet}>
          <section data-integrations-panel="wallet" hidden={onglet !== 'wallet'}>
            {boErr}{boLoading}
            {wallet.length > 0 && <CarteWallet settings={wallet} onWrite={write} />}
            <WalletPanel />
          </section>
        </IntegrationReportContext.Provider>
      </div>
    </div>
  );
}
